import { z } from 'zod';
import { coreIdSchema, EntityIdSchema } from './ids.js';

/**
 * A share request is the ask-side counterpart of a resource grant: a user
 * who cannot access a resource (today: another user's agent session) files
 * one, and the resource owner (or an org admin) resolves it by approving —
 * which opens a grant to the requester — or declining. Approval and
 * decline both keep the row as the audit record of the ask.
 */

export const SHARE_REQUEST_STATUSES = ['pending', 'approved', 'declined'] as const;
export const ShareRequestStatusSchema = z
  .enum(SHARE_REQUEST_STATUSES)
  .meta({ id: 'ShareRequestStatus' });
export type ShareRequestStatus = z.infer<typeof ShareRequestStatusSchema>;

export const ShareRequestSchema = z
  .object({
    id: coreIdSchema('shareRequest'),
    organizationId: coreIdSchema('organization'),
    resourceType: z.string(),
    resourceId: EntityIdSchema,
    /** The user asking for access. */
    requesterUserId: coreIdSchema('user'),
    /** Optional note the requester attached to the ask. */
    message: z.string().nullable(),
    status: ShareRequestStatusSchema,
    createdAt: z.number(),
    /** When the request left `pending`; `null` while it is open. */
    resolvedAt: z.number().nullable(),
    /** Who approved or declined it; `null` while it is open. */
    resolvedByUserId: coreIdSchema('user').nullable(),
    /**
     * Whether the CALLER may resolve this request: they own the requested
     * resource or hold the grant-admin tier. Computed per caller by the
     * routes.
     */
    canResolve: z.boolean().optional(),
  })
  .meta({ id: 'ShareRequest' });
export type ShareRequest = z.infer<typeof ShareRequestSchema>;

export const CreateShareRequestRequestSchema = z
  .object({
    resourceType: z.string().min(1).max(64),
    resourceId: EntityIdSchema,
    /** Optional note shown to the owner alongside the ask. */
    message: z.string().max(1000).optional(),
  })
  .meta({ id: 'CreateShareRequestRequest' });
export type CreateShareRequestRequest = z.infer<typeof CreateShareRequestRequestSchema>;

/**
 * Approving hands the requester one of the two sharing access levels the
 * share UI offers (see {@link resourceGrantRoleId} in
 * `resource-grant.types.ts`), conferred as the shared kind's own sharing
 * role.
 */
export const ApproveShareRequestRequestSchema = z
  .object({ access: z.enum(['view', 'edit']) })
  .meta({ id: 'ApproveShareRequestRequest' });
export type ApproveShareRequestRequest = z.infer<typeof ApproveShareRequestRequestSchema>;

export const ListShareRequestsQuerySchema = z.object({
  resourceType: z.string().optional(),
  resourceId: EntityIdSchema.optional(),
  status: ShareRequestStatusSchema.optional(),
});
export type ListShareRequestsQuery = z.infer<typeof ListShareRequestsQuerySchema>;

export const ShareRequestParamsSchema = z.object({ requestId: coreIdSchema('shareRequest') });
export type ShareRequestParams = z.infer<typeof ShareRequestParamsSchema>;

export const ShareRequestResponseSchema = z
  .object({
    request: ShareRequestSchema,
    /** On create: whether a new request was filed (`false` = an identical pending ask already existed). */
    created: z.boolean().optional(),
  })
  .meta({ id: 'ShareRequestResponse' });
export type ShareRequestResponse = z.infer<typeof ShareRequestResponseSchema>;

export const ShareRequestListResponseSchema = z
  .object({ requests: z.array(ShareRequestSchema) })
  .meta({ id: 'ShareRequestListResponse' });
export type ShareRequestListResponse = z.infer<typeof ShareRequestListResponseSchema>;
